import { useCallback, useEffect, useState } from "react";
import { Bot, Loader2, MessageSquare, Users, ThumbsUp, ThumbsDown, Flag, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const num = (n) => Number(n || 0).toLocaleString();

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-2xl border border-white/8 bg-[#121214] p-5" data-testid={testid}>
      <div className="w-9 h-9 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center mb-3"><Icon className="w-4 h-4 text-[#D4AF37]" /></div>
      <div className="font-heading text-2xl text-white tabular-nums">{value}</div>
      <div className="text-[#71717A] text-sm mt-0.5">{label}</div>
    </div>
  );
}

export default function AdminAI() {
  const [d, setD] = useState(null);
  const [denied, setDenied] = useState(false);
  const [s, setS] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/ai").then((r) => { setD(r.data); setS(r.data.settings); })
      .catch((e) => { if (e.response?.status === 403) setDenied(true); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    try { await api.put("/admin/ai/settings", s); toast.success("AI settings saved"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const review = async (id) => { try { await api.post(`/admin/ai/feedback/${id}/review`); toast.success("Marked reviewed"); load(); } catch { toast.error("Failed"); } };

  if (denied) return <p className="text-sm text-[#71717A]" data-testid="admin-ai-denied">You don't have the "ai.view" permission.</p>;
  if (!d) return <div className="flex justify-center py-20 text-[#71717A]"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div data-testid="admin-ai" className="space-y-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={MessageSquare} label="Conversations" value={num(d.usage.total_conversations)} testid="ai-conversations" />
        <Stat icon={Bot} label="Messages" value={num(d.usage.total_messages)} testid="ai-messages" />
        <Stat icon={MessageSquare} label="Conversations (30d)" value={num(d.usage.conversations_30d)} testid="ai-conv-30d" />
        <Stat icon={Users} label="Active users (30d)" value={num(d.usage.active_users_30d)} testid="ai-active-users" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <h3 className="text-white font-medium mb-3">Common questions</h3>
          <div className="rounded-2xl border border-white/8 bg-[#121214] divide-y divide-white/5" data-testid="ai-common-questions">
            {d.common_questions.length ? d.common_questions.map((q) => (
              <div key={q.question} className="flex items-center justify-between px-5 py-3 gap-4">
                <span className="text-[#D4D4D8] text-sm truncate">{q.question}</span>
                <span className="text-[#D4AF37] text-xs shrink-0">{q.count}×</span>
              </div>
            )) : <p className="px-5 py-6 text-sm text-[#71717A]">No questions yet.</p>}
          </div>
        </div>

        <div>
          <h3 className="text-white font-medium mb-3">AI settings & limits</h3>
          <div className="rounded-2xl border border-white/8 bg-[#121214] p-5 space-y-4" data-testid="ai-settings">
            <div className="flex items-center justify-between"><span className="text-[#D4D4D8] text-sm">AI features enabled</span>
              <Switch checked={s?.enabled} onCheckedChange={(v) => setS({ ...s, enabled: v })} data-testid="ai-enabled-toggle" /></div>
            <div><label className="text-xs text-[#71717A]">Daily message limit — Free tier</label>
              <Input type="number" value={s?.daily_limit_free ?? ""} onChange={(e) => setS({ ...s, daily_limit_free: parseInt(e.target.value, 10) || 0 })} data-testid="ai-limit-free" className="bg-[#0d0d0f] border-white/10 text-white mt-1" /></div>
            <div><label className="text-xs text-[#71717A]">Daily message limit — Paid tiers</label>
              <Input type="number" value={s?.daily_limit_paid ?? ""} onChange={(e) => setS({ ...s, daily_limit_paid: parseInt(e.target.value, 10) || 0 })} data-testid="ai-limit-paid" className="bg-[#0d0d0f] border-white/10 text-white mt-1" /></div>
            <Button onClick={saveSettings} data-testid="ai-save-settings" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full">Save settings</Button>
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center gap-4 mb-3">
          <h3 className="text-white font-medium">Feedback & flagged responses</h3>
          <div className="flex gap-3 text-xs">
            <span className="text-emerald-400 flex items-center gap-1"><ThumbsUp className="w-3 h-3" />{d.feedback.positive}</span>
            <span className="text-red-400 flex items-center gap-1"><ThumbsDown className="w-3 h-3" />{d.feedback.negative}</span>
            <span className="text-amber-400 flex items-center gap-1"><Flag className="w-3 h-3" />{d.feedback.flagged} flagged</span>
            <span className="text-[#71717A]">{d.feedback.unreviewed} unreviewed</span>
          </div>
        </div>
        <div className="space-y-2" data-testid="ai-feedback-list">
          {d.recent_feedback.length ? d.recent_feedback.map((f) => (
            <div key={f.feedback_id} className="rounded-xl border border-white/8 bg-[#121214] px-5 py-3 flex items-start justify-between gap-4" data-testid="ai-feedback-item">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  {f.flagged && <Flag className="w-3 h-3 text-amber-400" />}
                  <span className="text-xs text-[#71717A]">{f.rating || "—"}{f.reviewed ? " · reviewed" : ""}</span>
                </div>
                <p className="text-sm text-[#D4D4D8] line-clamp-2">{f.content || "(no content)"}</p>
              </div>
              {!f.reviewed && <Button onClick={() => review(f.feedback_id)} data-testid={`ai-review-${f.feedback_id}`} variant="outline" className="border-white/15 bg-transparent text-[#D4D4D8] rounded-full text-xs shrink-0"><Check className="w-3 h-3 mr-1" /> Review</Button>}
            </div>
          )) : <p className="text-sm text-[#71717A]">No feedback yet.</p>}
        </div>
      </div>
    </div>
  );
}
